import { gql } from '@apollo/client';
import type {
  CoachingFeedback,
  CoachingFeedbackAction,
  CoachingFeedbackStatus,
  EvidenceFileType,
  PageableContent,
  UploadCoachingEvidenceFileResponse,
  UUID,
} from '@cobliteam/common-types';
import { Environment } from '@cobliteam/react-environments';
import camelcaseKeys from 'camelcase-keys';
import snakecaseKeys from 'snakecase-keys';
import graphQLRequester from '../../request/GraphQLRequester';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import { toMediaView } from './mediaTransformer';

const env = Environment.getInstance().getEnvironment();

const coachingFeedbacksQuery = gql`
  query coachingFeedbackPage($input: CoachingFeedbackInput) {
    coachingFeedback(input: $input) {
      content {
        id
        driver {
          id
          name
        }
        created_at
        updated_by_user {
          full_name
        }
        updated_at
        feedback_action
        status
        created_by_user {
          full_name
        }
        coaching_evidences {
          id
          media_id
          safety_occurrences {
            id
            name
          }
        }
      }
      empty
      first
      last
      size
      number
      number_of_elements
      total_pages
      total_elements
    }
  }
`;

const coachingFeedbackDetailsQuery = gql`
  query coachingFeedbackDetails($id: ID!) {
    coachingFeedbackDetails(id: $id) {
      id
      driver {
        id
        name
      }
      created_at
      updated_at
      updated_by_user {
        full_name
      }
      status
      created_by_user {
        full_name
      }
      note
      note_created_at
      note_author {
        full_name
      }
      feedback_action
      coaching_evidences {
        id
        safety_occurrences {
          id
          name
        }
        files {
          id
          file_url
          file_type
          file_timestamp
          created_at
        }
        media_id
        media {
          media_url
          expiration_date
          videos {
            url
            channel
          }
          media_info {
            id
            device_id
            driver_id
            driver {
              name
            }
            media_type
            media_status
            is_favorite
            comments {
              id
              media_id
              created_by
              created_by_user {
                full_name
              }
              content
              created_at
              updated_at
            }
            safety_review {
              id
              fleet_id
              status
              driver_id
              driver {
                name
              }
              created_by
              updated_by
              updated_by_user {
                full_name
              }
              created_at
              updated_at
              occurrences {
                id
                name
              }
            }
            media_details {
              ... on EventVideo {
                event_trigger
                latitude
                longitude
                event_media_url: media_url
                speed
                speed_sign
                event_thumbnail_url: thumbnail_url
              }
              ... on FootageVideo {
                created_at
                duration
                end_latitude
                end_longitude
                footage_media_url: media_url
                request_id
                start_latitude
                start_longitude
                status
                footage_thumbnail_url: thumbnail_url
                updated_at
                created_by {
                  id
                  full_name
                  fleet_id
                  email_address
                }
                created_by_trigger {
                  id
                  name
                }
              }
            }
            media_view_count
            timestamp
            severity
            vehicle {
              id
              licensePlate
              groups {
                id
                name
              }
            }
          }
        }
      }
    }
  }
`;

export type GetCoachingFeedbackInput = {
  driverIds?: UUID[];
  mediaId?: UUID;
  page?: number;
  size?: number;
  safetyOccurrenceIds?: UUID[];
  sort?: string;
  status?: CoachingFeedbackStatus[];
  fromUpdatedAt?: string;
  toUpdatedAt?: string;
};

export type CreateCoachingFeedbackInput = {
  driverId: UUID;
  mediaIds: UUID[];
};

export type UpdateCoachingFeedbackInput = {
  coachingFeedbackId: UUID;
  mediaIds?: UUID[];
  status?: CoachingFeedbackStatus;
  note?: string;
  feedbackAction?: CoachingFeedbackAction;
  driverId?: UUID;
};

export type UploadEvidenceFileInput = {
  coachingEvidenceId: UUID;
  blobUrl: string;
  type: EvidenceFileType;
  timestamp?: number;
};

export type DeleteCoachingEvidenceFileInput = {
  coachingEvidenceId: UUID;
  evidenceFileId: UUID;
};

export class CoachingFeedbackRequester {
  private requester: Requester;

  constructor() {
    this.requester = new Requester({
      host: env.apiHost,
    });
  }

  public getCoachingFeedbacks = async (input?: GetCoachingFeedbackInput): Promise<PageableContent<CoachingFeedback>> => {
    const { coachingFeedback } = await graphQLRequester.query<{ coachingFeedback: PageableContent<CoachingFeedback> }>({
      query: coachingFeedbacksQuery,
      variables: {
        input,
      },
    });

    return camelcaseKeys(coachingFeedback, { deep: true });
  };

  public getCoachingFeedbackDetails = async (id: UUID): Promise<CoachingFeedback> => {
    const { coachingFeedbackDetails } = await graphQLRequester.query<{ coachingFeedbackDetails: any }>({
      query: coachingFeedbackDetailsQuery,
      variables: { id },
    });

    const transformed: CoachingFeedback = {
      ...coachingFeedbackDetails,
      coachingEvidences: coachingFeedbackDetails.coaching_evidences?.map((evidence: any) => ({
        ...evidence,
        media: evidence.media ? toMediaView(evidence.media) : undefined,
      })),
    };

    return camelcaseKeys(transformed, { deep: true });
  };

  public createCoachingFeedback = async (input: CreateCoachingFeedbackInput): Promise<CoachingFeedback> => {
    const { data } = await this.requester.request({
      method: HttpMethod.POST,
      endpoint: '/coaching/feedback',
      body: JSON.stringify(snakecaseKeys(input)),
    });

    return camelcaseKeys(data);
  };

  public updateCoachingFeedback = async (input: UpdateCoachingFeedbackInput): Promise<CoachingFeedback> => {
    const { coachingFeedbackId, ...request } = input;

    const { data } = await this.requester.request({
      method: HttpMethod.PATCH,
      endpoint: `/coaching/feedback/${coachingFeedbackId}`,
      body: JSON.stringify(snakecaseKeys(request)),
    });

    return camelcaseKeys(data, { deep: true });
  };

  public deleteCoachingFeedback = async (coachingFeedbackId: UUID): Promise<void> => {
    await this.requester.request({
      method: HttpMethod.DELETE,
      endpoint: `/coaching/feedback/${coachingFeedbackId}`,
    });
  };

  public uploadEvidenceFile = async ({ coachingEvidenceId, blobUrl, timestamp, type }: UploadEvidenceFileInput): Promise<UploadCoachingEvidenceFileResponse> => {
    const file = await fetch(blobUrl).then((response) => response.blob());
    const formData = new FormData();

    formData.append('file', file, 'capture.png');
    formData.append('type', type);
    if (timestamp) formData.append('fileTimestamp', timestamp.toString());

    const { data } = await this.requester.request({
      method: HttpMethod.PUT,
      endpoint: `/coaching/feedback/evidence/${coachingEvidenceId}/file`,
      body: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return camelcaseKeys(data);
  };

  public deleteEvidenceFile = async ({ coachingEvidenceId, evidenceFileId }: DeleteCoachingEvidenceFileInput): Promise<void> => {
    await this.requester.request({
      method: HttpMethod.DELETE,
      endpoint: `/coaching/feedback/evidence/${coachingEvidenceId}/file/${evidenceFileId}`,
    });
  };
}
